import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  params,
  shape,
  fragment,
  string as stringType,
  useDataModel,
  customType,
  arrayOf
} from '@thd-nucleus/data-sources';
import classNames from 'classnames';
import { HotspotDrawerContainer } from './hotspot-drawer/HotspotDrawerContainer';
import { HotspotBlock } from './HotspotBlock';
import { VideoPlayer } from './VideoPlayer';
import { getHotspotsData } from '../utils/getHotspotsData';

const { string, number, node } = PropTypes;

const renderResponsiveImage = (ssrImageData, title = '', enableB2CRadius = false, lazyLoadImages = false) => {
  const imageRadiusClass = classNames('sui-w-full', { 'sui-rounded-[12px]': enableB2CRadius });

  return (
    <picture className={imageRadiusClass}>
      {ssrImageData?.responsiveImages?.map(({ imageSrc, mediaQuery }, index) => (
        <source
          key={index}
          className={imageRadiusClass}
          alt={title}
          srcSet={imageSrc}
          media={mediaQuery}
        />
      ))}
      {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
      <img
        className={imageRadiusClass}
        alt={title}
        src={ssrImageData?.mobileImageSrc}
        loading={lazyLoadImages ? 'lazy' : 'eager'}
        // eslint-disable-next-line react/no-unknown-property
        fetchpriority={lazyLoadImages ? 'low' : 'high'}
      />
    </picture>
  );
};

const ImageHotspot = ({
  componentId = null, componentClass = 'ImageHotspot', position = 1, ssrImageData = null, enableB2CRadius = false,
  sourceData = null, lazyLoadImages = false, enableSSR = false, shouldLoopVideo = true,
  children = null
}) => {
  useEffect(() => {
    if (window.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('image-hotspot.ready');
    }
  }, []);

  const { data, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass,
    },
    ssr: enableSSR,
    skip: !!sourceData || !componentId
  });

  const componentData = data?.component;

  if (!sourceData && ((error || (!componentData && !ssrImageData)))) {
    return null;
  }

  const { hotspotArr, actions } = getHotspotsData({ componentData, sourceData });

  const renderPrimaryContent = () => {
    if (children) return children;

    const finalDesktopUrl = ssrImageData?.responsiveImages?.[0]?.imageSrc;
    const finalMobileUrl = ssrImageData?.mobileImageSrc;
    const mobilePosterImageUrl = ssrImageData?.mobilePosterImageUrl || null;
    const desktopPosterImageUrl = ssrImageData?.desktopPosterImageUrl || null;

    const desktopVideoUrl = finalDesktopUrl && finalDesktopUrl.toLowerCase().endsWith('.mp4') ? finalDesktopUrl : null;

    const mobileVideoUrl = finalMobileUrl && finalMobileUrl.toLowerCase().endsWith('.mp4') ? finalMobileUrl : null;

    const hasVideo = !!(desktopVideoUrl || mobileVideoUrl);

    const title = sourceData?.title || componentData?.title || '';

    if (hasVideo) {
      // eslint-disable-next-line
      const videoClasses = classNames('sui-w-full sui-h-full sui-object-cover', { 'sui-rounded-[12px]': enableB2CRadius });

      return (
        <VideoPlayer
          desktopVideoUrl={desktopVideoUrl}
          mobileVideoUrl={mobileVideoUrl}
          altText={title}
          videoClasses={videoClasses}
          mobilePosterImageUrl={mobilePosterImageUrl}
          desktopPosterImageUrl={desktopPosterImageUrl}
          shouldLoopVideo={shouldLoopVideo}
          prioritizeMedia={!lazyLoadImages}
        />
      );
    }

    if (!ssrImageData) {
      const fetchPriority = lazyLoadImages ? 'low' : 'high';
      const imageLoadingPriority = lazyLoadImages ? 'lazy' : 'eager';
      return (
        <>
          {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
          <img
            className={classNames('sui-w-full', { 'sui-rounded-[12px]': enableB2CRadius })}
            src={sourceData?.uri || componentData?.imageUrl}
            alt={title}
            loading={imageLoadingPriority}
            // eslint-disable-next-line react/no-unknown-property
            fetchpriority={fetchPriority}
          />
        </>
      );
    }

    return renderResponsiveImage(ssrImageData, title, enableB2CRadius, lazyLoadImages);
  };

  const renderHotspots = () => {
    if (!hotspotArr) {
      return null;
    }

    return hotspotArr.map((hotspot, idx) => {
      const hotspotStyle = {
        width: `${hotspot.width}%`,
        height: `${hotspot.height}%`,
        left: `${hotspot.x}%`,
        top: `${hotspot.y}%`,
        zIndex: 10,
      };

      if (componentData) {
        const action = actions?.filter((actionItem) => actionItem.title === hotspot.name);

        if (action?.[0]?.drawer?.headline) {
          return (
            <HotspotDrawerContainer
              componentId={componentId}
              key={`hotspot-${idx}-${action[0].id}`}
              style={hotspotStyle}
              headline={action[0]?.drawer?.headline}
            />
          );
        }

        if (action?.[0]?.id && action[0].url) {
          return (
            <HotspotBlock
              key={`hotspot-${idx}-${action[0].id}`}
              id={action[0].id}
              position={position}
              style={hotspotStyle}
              action={action[0].url}
              name={hotspot.name}
            />
          );
        }
      }
      if (sourceData) {
        if (hotspot?.drawer && Array.isArray(hotspot?.drawer)) {
          return (
            <HotspotDrawerContainer
              componentId={componentId}
              key={`hotspot-${idx}-${sourceData.entryId}`}
              style={hotspotStyle}
              headline={hotspot?.name}
              drawerData={hotspot?.drawer}
            />
          );
        }
        if (actions[idx]) {
          return (
            <HotspotBlock
              key={`hotspot-${idx}-${sourceData.entryId}`}
              id={sourceData.entryId}
              // Because sourceData passes entire array down,
              // and hotspot comes from the same entryId
              // the position value will need to be used as the unique value.
              // Adding +1 for reporting.
              position={idx + 1}
              style={hotspotStyle}
              action={actions[idx]}
              name={hotspot.name}
            />
          );
        }
      }
      return null;
    });
  };

  return (
    <div
      className="sui-relative sui-mx-auto sui-my-0 sui-w-full sui-h-full"
      data-component="ImageHotspot"
    >
      {renderPrimaryContent()}
      {renderHotspots()}
    </div>
  );
};

ImageHotspot.propTypes = {
  componentId: string,
  componentClass: string,
  position: PropTypes.oneOfType([string, number]),
  ssrImageData: PropTypes.shape({
    mobileImageSrc: string,
    mobilePosterImageUrl: string,
    desktopPosterImageUrl: string,
    responsiveImages: PropTypes.arrayOf(
      PropTypes.shape({
        imageSrc: string,
        mediaQuery: string,
      })
    ),
  }),
  enableB2CRadius: PropTypes.bool,
  lazyLoadImages: PropTypes.bool,
  enableSSR: PropTypes.bool,
  sourceData: PropTypes.shape({
    entryId: string,
    type: string,
    uri: string,
    altText: string,
    hyperlink: string,
    hotspot: string,
    title: string,
    dimensions: string
  }),
  shouldLoopVideo: PropTypes.bool,
  children: node,
};

ImageHotspot.displayName = 'ImageHotspot';

ImageHotspot.dataModel = {
  component: params({
    componentClass: customType('ComponentClass').enum(['ImageHotspot']).isRequired(),
    id: stringType().isRequired(),
  }).shape({
    ImageHotspot: fragment().shape({
      id: stringType(),
      title: stringType(),
      imageUrl: stringType(),
      hotspots: stringType(),
      hotspotActions: arrayOf(shape({
        id: stringType(),
        url: stringType(),
        title: stringType(),
        drawer: shape({
          headline: stringType(),
        }),
      }))
    })
  })
};

export { ImageHotspot, HotspotBlock };
